import {
	Component,
	DestroyRef,
	inject,
	Injector,
	OnInit,
	Signal,
	ViewChild,
	ViewEncapsulation,
	afterNextRender,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { DashboardComponent, IToolbar, ToolbarComponent } from '@tecalisdev/infinity';
import { LineChart } from 'echarts/charts';
import * as echarts from 'echarts/core';
import { ToolbarHelperService } from '../shared/services/toolbar-helper.service';
import { InProgressProcessesComponent } from './components/in-progress-processes/in-progress-processes.component';
import { ManualValidationProcessesComponent } from './components/manual-validation-processes/manual-validation-processes.component';
import { NavigationCardsComponent } from './components/navigation-cards/navigation-cards.component';
import { OperationBalanceAlertComponent } from './components/operation-balance-alert/operation-balance-alert.component';
import { ProcessChartComponent } from './components/process-chart/process-chart.component';
import { StatsComponent } from './components/stats/stats.component';
import { PanelInsightsComponent } from './components/panel-insights/panel-insights.component';
import { PanelHelperService } from './services/panel-helper.service';
import { PanelService } from './services/panel.service';

@Component({
	selector: 'app-panel',
	imports: [
		DashboardComponent,
		StatsComponent,
		ToolbarComponent,
		ProcessChartComponent,
		PanelInsightsComponent,
		InProgressProcessesComponent,
		ManualValidationProcessesComponent,
		OperationBalanceAlertComponent,
		NavigationCardsComponent,
	],
	templateUrl: './panel.component.html',
	styleUrl: './panel.component.scss',
	encapsulation: ViewEncapsulation.None,
})
export class PanelComponent implements OnInit {
	private panelHelperService = inject(PanelHelperService);
	private panelService = inject(PanelService);
	private toolbarHelperService = inject(ToolbarHelperService);
	private trans = inject(TranslateService);
	private destroyRef = inject(DestroyRef);
	private injector = inject(Injector);

	@ViewChild('datesToolbar') public toolbarComponent?: ToolbarComponent;

	public toolbar: Signal<IToolbar>;
	public config: { pagination?: any } = {};
	public balanceStatus: 'ok' | 'low' | 'depleted' = 'ok';

	ngOnInit(): void {
		// Sin persistencia al navegar: el panel siempre arranca con el toolbar por defecto del helper.
		this.toolbarHelperService.initializeListToolbar(this, () => this.panelHelperService.toolbar, undefined, true);
		echarts.use([LineChart]);
		// Iniciar con filtros por defecto (hoy)
		this.panelHelperService.updateFilters({ period: { value: 'today', id: 1 } });

		// Actualizar el chip del periodo seleccionado cuando cambia el idioma
		this.trans.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			const periodId = this.panelHelperService.currentFilters()?.periodId;
			if (periodId !== undefined && this.toolbarComponent) {
				const periodFilter = this.toolbar().filters?.find((f: any) => f.name === 'period');
				const newOption = periodFilter?.options?.find((o: any) => o.id === periodId);
				if (newOption) {
					// Macrotick aparte: evita NG0100 en _ToolbarComponent al actualizar el chip del periodo.
					setTimeout(() => {
						this.toolbarComponent?.form.get('period')?.setValue(newOption, { emitEvent: false });
					}, 0);
				}
			}
		});
	}

	public filterChange(filters: any): void {
		const next = { ...filters };
		const current = this.panelHelperService.currentFilters() ?? {};

		// Comparar por ID para detectar cambios reales en period
		const periodChanged = next.period?.id !== current.periodId;
		const startAtChanged = next.start_at && next.start_at !== current.start_at;
		const endAtChanged = next.end_at && next.end_at !== current.end_at;

		let deferClearDatesOnToolbar = false;
		let deferClearPeriodOnToolbar = false;

		if (periodChanged && next.period) {
			next.start_at = null;
			next.end_at = null;
			deferClearDatesOnToolbar = true;
		} else if (periodChanged && !next.period && current.periodId) {
			next.start_at = null;
			next.end_at = null;
			deferClearDatesOnToolbar = true;
		} else if (startAtChanged || endAtChanged) {
			delete next.period;
			deferClearPeriodOnToolbar = true;
		}

		this.panelHelperService.updateFilters(next);

		// No mutar el FormGroup en el mismo turno que formFilter del tec-toolbar (NG0100).
		if (deferClearDatesOnToolbar) {
			this.scheduleToolbarFormWrite(() => this.clearToolbarDateFieldsOnly());
		}
		if (deferClearPeriodOnToolbar) {
			this.scheduleToolbarFormWrite(() => {
				this.toolbarComponent?.form.get('period')?.setValue(null, { emitEvent: false });
			});
		}
	}

	/** Ejecuta escrituras en el FormGroup del tec-toolbar tras el siguiente render (evita NG0100). */
	private scheduleToolbarFormWrite(fn: () => void): void {
		afterNextRender(
			() => {
				fn();
			},
			{ injector: this.injector },
		);
	}

	/** Vacía Desde/Hasta en el FormGroup tras un tick (preset o deselección de periodo). */
	private clearToolbarDateFieldsOnly(): void {
		const form = this.toolbarComponent?.form as FormGroup | undefined;
		if (!form) return;
		form.patchValue({ start_at: null, end_at: null }, { emitEvent: false });
	}
}
